import { useUsersForm } from '@modules/users/hooks/useUsersForm';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { personService } from '@shared/services/personService';

export default function UsersForm({ userId, onClose, selectedData, fetchData }) {
    const { t } = useTranslation();
    const [persons, setPersons] = useState([]);
    
    const {
        modalRef,
        register,
        handleSubmit,
        errors,
        setValue,
        serverMessage,
        groups,
        onSubmit,
        generatePassword,
        passwordType,
        confirmPasswordType,
        passwordIcon,
        confirmPasswordIcon,
        togglePasswordVisibility,
        toggleConfirmPasswordVisibility,
        selectedGroup,
        setSelectedGroup,
        selectedPerson,
        setSelectedPerson,
        loading,
        isEdit,
    } = useUsersForm({
        userId,
        onClose,
        selectedData,
        fetchData
    });

    useEffect(() => {
        const fetchPersons = async () => {
            try {
                const data = await personService.getPersons({
                    pageNumber: 0,
                    pageSize: 1000, // Fetch a large number to get all persons
                    sortBy: 'name',
                    sortDir: 'asc'
                });
                setPersons(data.content || []);
            } catch (error) {
                console.error('Error fetching persons:', error);
                setPersons([]);
            }
        };

        fetchPersons();
    }, []);

    if (loading) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="form-wrapper width-50 user-form">
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className='modal show' tabIndex="-1">
            <div className="form-wrapper width-50 user-form">
                <form className="form" ref={modalRef} onSubmit={handleSubmit(onSubmit)}>
                    <h2 className="title">
                        {isEdit ? t('users.table.headers.editUser') : t('users.table.headers.addUser')}
                    </h2>
                    <div className="form-container">
                        <div className="form-data-container">
                            <div className="form-column">
                                <div className="form-input-wrapper">
                                    <span className="span-bold">{t('persons.form.username')} <span className="required">*</span></span>
                                    <div className="form-input-fields">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            placeholder={t('users.table.headers.username')}
                                            {...register('username')}
                                        />
                                        {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
                                        {serverMessage && <div className="invalid-feedback">{serverMessage}</div>}
                                    </div>
                                </div>
                                <div className="form-input-wrapper">
                                    <span className="span-bold">{t('persons.form.password')} {!isEdit && <span className="required">*</span>}</span>
                                    <div className="form-input-fields">
                                        <div className="password-input-container mb-3">
                                            <input
                                                type={passwordType}
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                placeholder="******"
                                                {...register('password')}
                                            />
                                            <button 
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={togglePasswordVisibility}
                                            >
                                                <FontAwesomeIcon icon={passwordIcon} />
                                            </button>
                                            {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                                        </div>
                                        
                                        <div className="password-input-container mb-3">
                                            <input
                                                type={confirmPasswordType}
                                                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                placeholder={t('persons.form.confirmPassword')}
                                                {...register('confirmPassword')}
                                            />
                                            <button 
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={toggleConfirmPasswordVisibility}
                                            >
                                                <FontAwesomeIcon icon={confirmPasswordIcon} />
                                            </button>
                                            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword.message}</div>}
                                        </div>
                                        
                                        <button 
                                            type="button"
                                            className="generate-password-btn"
                                            onClick={generatePassword}
                                        >
                                            {t('persons.form.generatePassword')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-column">
                            <div className="system-access-controls">
                                <div className="access-row">
                                    <div className="form-group">
                                        <span className="span-bold">{t('persons.form.employeeNumber')}</span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.employeeNumber ? 'is-invalid' : ''}`}
                                            name="employeeNumber"
                                            {...register('employeeNumber')}
                                            placeholder={t('persons.form.employeeNumber')}
                                        />
                                        {errors.employeeNumber && <div className="invalid-feedback">{errors.employeeNumber.message}</div>}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('users.table.headers.description')}</span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            name="description"
                                            {...register('description')}
                                            placeholder={t('users.form.descriptionPlaceholder')}
                                        />
                                        {/* {errors.description && <div className="invalid-feedback">{errors.description.message}</div>} */}
                                    </div>
                                    <span className="span-bold">{t('nav.administrator.adminSection.userGroups')} <span className="required">*</span></span>
                                    <div className="special-rights-wrapper">
                                        <select
                                            className={`form-control mb-2 ${errors.groupId ? 'is-invalid' : ''}`}
                                            name="groupId"
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setSelectedGroup(newValue);
                                                setValue('groupId', newValue, { shouldValidate: true });
                                            }}
                                            value={selectedGroup}
                                        >
                                            <option value="" disabled>
                                                {t('persons.form.optionSelect')}
                                            </option>
                                            {Array.isArray(groups) && groups.length > 0 && groups.map((group) => (
                                                <option key={group.value} value={group.value}>
                                                    {group.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.groupId && <div className="invalid-feedback">{errors.groupId.message}</div>}
                                        <span className="span-bold">{t('nav.administrator.adminSection.selectPerson')} <span className="required">*</span></span>
                                        <select
                                            className={`form-control mb-2 ${errors.personId ? 'is-invalid' : ''}`}
                                            name="personId"
                                            value={selectedPerson}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                setSelectedPerson(newValue);
                                                setValue('personId', newValue);
                                            }}
                                        >
                                            <option value="" disabled>
                                                {t('persons.form.optionSelect')}
                                            </option>
                                            {persons && persons.map(person => (
                                                <option key={person.id} value={person.id}>
                                                    {[person.name, person.fathersName, person.surname]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.personId && <div className="invalid-feedback">{errors.personId?.message}</div>}
                                    </div>
                                    
                                </div>

                                <div className="access-options">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="isLocked"
                                            {...register('isLocked')}
                                        />
                                        <label className="form-check-label">{t('users.table.headers.locked')}</label>
                                    </div>
                                    <div className="form-check ldap-section">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="ldapAccess"
                                            {...register('ldapAccess')}
                                        />
                                        <label className="form-check-label">{t('users.table.headers.ldapAccess')}</label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="extendedAccess"
                                            {...register('extendedAccess')}
                                        />
                                        <label className="form-check-label">{t('users.table.headers.extendedAccess')}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-controls-container">
                        <button type="submit" className="form-btn btn-blue">
                            {t('common.save')}
                        </button>
                        <button type="button" className="form-btn btn-red" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </form >
            </div >
        </div>
    );
}